import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID, REQUEST, TransferState } from '@angular/core';
import { TRANSFER_STATE_KEYS } from '@app/core/constants/transfer-state-keys';
import { DomainResolverService } from '@app/services/domain-resolver.service';
import { AgencySocialLinks, AgencySocialLinksResponse, unwrapSocialLinks } from '@back/models/agency';
import { API_URL } from '@core/tokens/api.token';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { catchError, finalize, Observable, of, shareReplay, TimeoutError } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class SocialMediaLinksService {
  private readonly apiUrl = inject(API_URL);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly http = inject(HttpClient);
  private readonly cookieService = inject(SsrCookieService);

  getAgencySocialLinks(): Observable<AgencySocialLinks> {
    let linksCookie = this.cookieService.get('social_links').trim();
    if (linksCookie) {
      try {
        const parsedLinks = JSON.parse(linksCookie);
        // Array check also discards cookies written by the old fixed-fields shape
        if (Array.isArray(parsedLinks)) {
          return of(parsedLinks as AgencySocialLinks);
        }
        this.cookieService.delete('social_links');
      } catch (error) {
        this.cookieService.delete('social_links');
        console.error('Error parsing social_links cookie:', error);
      }
    }

    return this.http.get<AgencySocialLinksResponse>(`${this.apiUrl}/frontend/agency/social`).pipe(
      map(unwrapSocialLinks),
      tap(data => {
        // Cache successful responses only if not empty
        if (data.length) {
          this.setCookie(data);
        }
      }),
      catchError(err => {
        console.error('Error fetching agency social links:', err);
        return of([] as AgencySocialLinks);
      })
    );
  }

  /** Called after the agency edits its links, so the next read refetches instead of serving the week-old cookie. */
  clearCache(): void {
    this.cookieService.delete('social_links', '/');
  }

  private setCookie(data: AgencySocialLinks) {
    try {
      const cookieValue = JSON.stringify(data);
      this.cookieService.set('social_links', cookieValue, {
        path: '/',
        secure: true,
        sameSite: 'Lax',
        expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 1 week
      });
    } catch (error) {
      console.error('Error setting social_links cookie:', error);
    }
  }
}
